import { useState, useMemo } from "react";
import { useSeoMeta } from "@unhead/react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Lock,
  Users,
  UserCheck,
  CheckCircle2,
  GraduationCap,
  Share2,
  Wallet,
  RefreshCw,
  BrainCircuit,
  Heart,
  TrendingUp,
  Repeat,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from "recharts";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getLesson } from "@/lib/curriculum";

interface Metrics {
  uniqueStudents: number;
  returningStudents: number;
  lessonsCompleted: number;
  certificatesEarned: number;
  avgLessonsPerStudent: number;
  /** 0..1, or null if no eligible cohort yet. */
  retention7d: number | null;
  retentionCohortSize: number;
}

interface Counters {
  totals: Record<string, number>;
  daily: Record<string, Record<string, number>>;
  lessons: Record<string, number>;
  metrics?: Metrics;
  startedAt: string;
}

export default function StatsPage() {
  const [passcode, setPasscode] = useState("");
  const [data, setData] = useState<Counters | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useSeoMeta({
    title: "Stats — BCH School",
    description: "Private stats dashboard for BCH School.",
  });

  async function load(code: string) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/.netlify/functions/stats?action=read&passcode=${encodeURIComponent(code)}`,
      );
      if (res.status === 401) {
        setError("Wrong passcode. Try again.");
        setData(null);
        return;
      }
      if (!res.ok) {
        setError(
          "Couldn't load stats. Make sure the site is deployed to Netlify.",
        );
        return;
      }
      const json = (await res.json()) as Counters;
      setData(json);
    } catch {
      setError(
        "Couldn't reach the stats service. This only works on the deployed Netlify site.",
      );
    } finally {
      setLoading(false);
    }
  }

  const dailyRows = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.daily)
      .map(([date, ev]) => ({ date: date.slice(5), visits: ev.visit ?? 0 }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-14);
  }, [data]);

  // Derived audience metrics come from the backend. Fall back to zeros for
  // older deployments that don't return a metrics object yet.
  const metrics: Metrics = useMemo(
    () =>
      data?.metrics ?? {
        uniqueStudents: 0,
        returningStudents: 0,
        lessonsCompleted: 0,
        certificatesEarned: data?.totals.certificate_earned ?? 0,
        avgLessonsPerStudent: 0,
        retention7d: null,
        retentionCohortSize: 0,
      },
    [data],
  );

  const topLessons = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.lessons)
      .map(([id, opens]) => ({
        id,
        title: getLesson(id)?.title ?? id,
        opens,
      }))
      .sort((a, b) => b.opens - a.opens)
      .slice(0, 8);
  }, [data]);

  return (
    <Layout>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/">
          <ArrowLeft className="mr-1 h-4 w-4" /> Home
        </Link>
      </Button>

      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight">Stats</h1>
        <p className="mt-1 text-muted-foreground">
          Private dashboard — see how BCH School is doing.
        </p>
      </div>

      {!data ? (
        <Card className="mx-auto max-w-md border-2">
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center gap-2 text-primary">
              <Lock className="h-5 w-5" />
              <h2 className="font-bold">Enter passcode</h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (passcode.trim()) load(passcode.trim());
              }}
              className="space-y-3"
            >
              <div>
                <Label htmlFor="passcode">Passcode</Label>
                <Input
                  id="passcode"
                  type="password"
                  className="mt-1"
                  placeholder="••••••••"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="mr-2 h-4 w-4" />
                )}
                View stats
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Audience metrics */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              icon={<Users className="h-5 w-5" />}
              label="Unique students"
              value={metrics.uniqueStudents}
              hint="Distinct anonymous learners"
            />
            <StatCard
              icon={<CheckCircle2 className="h-5 w-5" />}
              label="Lessons completed"
              value={metrics.lessonsCompleted}
            />
            <StatCard
              icon={<UserCheck className="h-5 w-5" />}
              label="Returning students"
              value={metrics.returningStudents}
              hint="Active on 2+ different days"
            />
            <StatCard
              icon={<GraduationCap className="h-5 w-5" />}
              label="Certificates earned"
              value={metrics.certificatesEarned}
            />
            <StatCard
              icon={<TrendingUp className="h-5 w-5" />}
              label="Avg lessons / student"
              value={metrics.avgLessonsPerStudent}
            />
            <StatCard
              icon={<Repeat className="h-5 w-5" />}
              label="7-day retention"
              value={
                metrics.retention7d === null
                  ? "—"
                  : `${Math.round(metrics.retention7d * 100)}%`
              }
              hint={
                metrics.retention7d === null
                  ? "Not enough data yet"
                  : `From ${metrics.retentionCohortSize.toLocaleString()} eligible`
              }
            />
          </div>

          {/* Engagement counters */}
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Engagement
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                icon={<Share2 className="h-5 w-5" />}
                label="Share clicks"
                value={data.totals.share_click ?? 0}
              />
              <StatCard
                icon={<BrainCircuit className="h-5 w-5" />}
                label="Quiz passes"
                value={data.totals.quiz_pass ?? 0}
              />
              <StatCard
                icon={<Wallet className="h-5 w-5" />}
                label="Wallet clicks"
                value={data.totals.wallet_click ?? 0}
              />
              <StatCard
                icon={<Heart className="h-5 w-5" />}
                label="Tip clicks"
                value={data.totals.donate_click ?? 0}
              />
            </div>
          </div>

          {/* Visits over time */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="text-base">
                Visits — last {dailyRows.length} days
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dailyRows.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No daily data yet.
                </p>
              ) : (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyRows}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="date" fontSize={12} />
                      <YAxis allowDecimals={false} fontSize={12} />
                      <RechartsTooltip />
                      <Bar
                        dataKey="visits"
                        fill="hsl(145 80% 33%)"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top lessons */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="text-base">Most-opened lessons</CardTitle>
            </CardHeader>
            <CardContent>
              {topLessons.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No lesson data yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {topLessons.map((l) => (
                    <li
                      key={l.id}
                      className="flex items-center justify-between gap-3 rounded-lg bg-secondary/50 px-3 py-2"
                    >
                      <span className="truncate text-sm font-medium">
                        {l.title}
                      </span>
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary tabular-nums">
                        {l.opens}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Counting since{" "}
              {new Date(data.startedAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(passcode.trim())}
              disabled={loading}
            >
              <RefreshCw
                className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <Card className="border-2">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-primary">{icon}</div>
        <div className="mt-2 text-3xl font-extrabold tabular-nums">
          {typeof value === "number" ? value.toLocaleString() : value}
        </div>
        <div className="text-sm text-muted-foreground">{label}</div>
        {hint && (
          <div className="mt-1 text-xs text-muted-foreground/70">{hint}</div>
        )}
      </CardContent>
    </Card>
  );
}
